import React, { useCallback, useEffect, useRef, useState } from "react";

// Кадр открытой истории — 375×150. Пунктиром показываем, что от кадрированной
// фотографии останется там: она вписывается по cover, поэтому лишнее обрежется.
const DETAIL_ASPECT = 375 / 150;

const PRESETS = [
	{ key: "cover", label: "Обложка 327×138", aspect: 327 / 138 },
	{ key: "story", label: "История 375×150", aspect: DETAIL_ASPECT },
	{ key: "free", label: "Свободно", aspect: null },
];

// Больше не увеличиваем: апскейл ничего не добавляет, только вес.
const MAX_OUT = 2000;
const MIN_CROP = 40;

const CORNERS = ["nw", "ne", "sw", "se"];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const PhotoCropper = ({
	file,
	position,
	total,
	onSkip,
	onCancelAll,
	onConfirm,
	onUploadOriginal,
}) => {
	const [src, setSrc] = useState(null);
	const [natural, setNatural] = useState(null);
	const [view, setView] = useState(0);
	const [rect, setRect] = useState(null);
	const [preset, setPreset] = useState("cover");
	const [isBusy, setIsBusy] = useState(false);

	const imgRef = useRef(null);
	const dragRef = useRef(null);

	const aspect = PRESETS.find((p) => p.key === preset)?.aspect ?? null;

	useEffect(() => {
		const url = URL.createObjectURL(file);
		setSrc(url);
		setNatural(null);
		setRect(null);

		return () => URL.revokeObjectURL(url);
	}, [file]);

	const measure = useCallback(() => {
		if (imgRef.current) setView(imgRef.current.clientWidth);
	}, []);

	useEffect(() => {
		window.addEventListener("resize", measure);
		return () => window.removeEventListener("resize", measure);
	}, [measure]);

	/** Наибольшая рамка заданной пропорции, влезающая в картинку. */
	const fit = (w, h, ratio) => {
		if (!ratio) return { w, h };

		let cw = w;
		let ch = cw / ratio;
		if (ch > h) {
			ch = h;
			cw = ch * ratio;
		}
		return { w: cw, h: ch };
	};

	const onImageLoad = (event) => {
		const w = event.target.naturalWidth;
		const h = event.target.naturalHeight;
		setNatural({ w, h });

		const size = fit(w, h, PRESETS[0].aspect);

		// Лица почти всегда в верхней половине кадра, поэтому стартуем выше
		// центра: так реже приходится двигать рамку вручную.
		setRect({
			x: (w - size.w) / 2,
			y: clamp((h - size.h) * 0.25, 0, h - size.h),
			w: size.w,
			h: size.h,
		});

		measure();
	};

	const onPreset = (key) => {
		setPreset(key);

		const next = PRESETS.find((p) => p.key === key)?.aspect ?? null;
		if (!next || !rect || !natural) return;

		const size = fit(natural.w, natural.h, next);
		const w = Math.min(rect.w, size.w);
		const h = w / next;
		const cx = rect.x + rect.w / 2;
		const cy = rect.y + rect.h / 2;

		setRect({
			w,
			h,
			x: clamp(cx - w / 2, 0, natural.w - w),
			y: clamp(cy - h / 2, 0, natural.h - h),
		});
	};

	const scale = natural && view ? view / natural.w : 0;

	const startDrag = (event, corner) => {
		if (!rect) return;

		event.stopPropagation();
		event.currentTarget.setPointerCapture(event.pointerId);
		dragRef.current = { px: event.clientX, py: event.clientY, rect, corner };
	};

	const onPointerMove = (event) => {
		const drag = dragRef.current;
		if (!drag || !natural || !scale) return;

		const dx = (event.clientX - drag.px) / scale;
		const dy = (event.clientY - drag.py) / scale;
		const s = drag.rect;

		if (!drag.corner) {
			setRect({
				...s,
				x: clamp(s.x + dx, 0, natural.w - s.w),
				y: clamp(s.y + dy, 0, natural.h - s.h),
			});
			return;
		}

		let { x, y, w, h } = s;
		const west = drag.corner.includes("w");
		const north = drag.corner.includes("n");

		if (west) {
			x = s.x + dx;
			w = s.w - dx;
		} else {
			w = s.w + dx;
		}

		if (north) {
			y = s.y + dy;
			h = s.h - dy;
		} else {
			h = s.h + dy;
		}

		if (aspect) {
			// При закреплённой пропорции ведущей берём ширину, а высоту
			// подгоняем, удерживая противоположный угол на месте.
			h = w / aspect;
			if (north) y = s.y + s.h - h;
		}

		w = clamp(w, MIN_CROP, natural.w);
		h = clamp(h, MIN_CROP, natural.h);

		if (aspect) {
			h = w / aspect;
			if (h > natural.h) {
				h = natural.h;
				w = h * aspect;
			}
			if (north) y = s.y + s.h - h;
			if (west) x = s.x + s.w - w;
		}

		setRect({
			w,
			h,
			x: clamp(x, 0, natural.w - w),
			y: clamp(y, 0, natural.h - h),
		});
	};

	const onPointerUp = () => {
		dragRef.current = null;
	};

	const onZoom = (event) => {
		const w = Number(event.target.value);
		const h = aspect ? w / aspect : w * (rect.h / rect.w);
		const cx = rect.x + rect.w / 2;
		const cy = rect.y + rect.h / 2;

		setRect({
			w,
			h,
			x: clamp(cx - w / 2, 0, natural.w - w),
			y: clamp(cy - h / 2, 0, natural.h - h),
		});
	};

	const confirm = () => {
		if (!rect || !imgRef.current) return;

		setIsBusy(true);
		try {
			const outW = Math.round(Math.min(rect.w, MAX_OUT));
			const canvas = document.createElement("canvas");
			canvas.width = outW;
			canvas.height = Math.max(1, Math.round((outW * rect.h) / rect.w));

			const ctx = canvas.getContext("2d");
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.drawImage(
				imgRef.current,
				rect.x, rect.y, rect.w, rect.h,
				0, 0, canvas.width, canvas.height,
			);

			const url = canvas.toDataURL("image/jpeg", 0.92);
			onConfirm(url.slice(url.indexOf(",") + 1));
		} finally {
			setIsBusy(false);
		}
	};

	// Кадр истории шире — значит обрежется по высоте; будь он уже, обрезалось
	// бы по ширине. Считаем обе стороны, чтобы подсказка была верной при любой
	// пропорции кадрирования.
	const cropAspect = rect ? rect.w / rect.h : 1;
	const detailBox = {
		width: `${100 * Math.min(1, DETAIL_ASPECT / cropAspect)}%`,
		height: `${100 * Math.min(1, cropAspect / DETAIL_ASPECT)}%`,
	};

	const maxW = natural ? fit(natural.w, natural.h, aspect).w : 0;

	return (
		<div className="fscrop">
			<div className="fscrop__box">
				<div className="fscrop__head">
					<strong>Кадрирование</strong>
					<span>
						{file.name} · {position} из {total}
					</span>
				</div>

				<div className="fscrop__presets">
					{PRESETS.map((p) => (
						<button
							key={p.key}
							type="button"
							className={p.key === preset ? "isActive" : ""}
							onClick={() => onPreset(p.key)}
						>
							{p.label}
						</button>
					))}
				</div>

				<div className="fscrop__stage">
					<img
						ref={imgRef}
						src={src}
						alt=""
						className="fscrop__image"
						onLoad={onImageLoad}
						draggable={false}
					/>

					{rect && scale > 0 && (
						<div
							className="fscrop__frame"
							style={{
								left: rect.x * scale,
								top: rect.y * scale,
								width: rect.w * scale,
								height: rect.h * scale,
							}}
							onPointerDown={(e) => startDrag(e, null)}
							onPointerMove={onPointerMove}
							onPointerUp={onPointerUp}
							onPointerCancel={onPointerUp}
						>
							<div className="fscrop__detail" style={detailBox} />

							{CORNERS.map((corner) => (
								<span
									key={corner}
									className={`fscrop__handle is-${corner}`}
									onPointerDown={(e) => startDrag(e, corner)}
									onPointerMove={onPointerMove}
									onPointerUp={onPointerUp}
									onPointerCancel={onPointerUp}
								/>
							))}
						</div>
					)}
				</div>

				<p className="fscrop__hint">
					Рамку можно перетаскивать, углы — тянуть. Пунктир внутри показывает,
					что останется в открытой истории.
					{rect && (
						<>
							{" "}
							Кадр: {Math.round(rect.w)}×{Math.round(rect.h)}
						</>
					)}
				</p>

				{rect && (
					<label className="fscrop__zoom">
						Масштаб
						<input
							type="range"
							min={MIN_CROP}
							max={Math.round(maxW)}
							value={Math.round(Math.min(rect.w, maxW))}
							onChange={onZoom}
						/>
					</label>
				)}

				<div className="fscrop__actions">
					<button type="button" onClick={confirm} disabled={!rect || isBusy}>
						Обрезать и загрузить
					</button>
					<button type="button" onClick={onUploadOriginal} disabled={isBusy}>
						Загрузить как есть
					</button>
					<button type="button" onClick={onSkip} disabled={isBusy}>
						Пропустить
					</button>
					<button
						type="button"
						className="fscrop__cancel"
						onClick={onCancelAll}
						disabled={isBusy}
					>
						Отменить всё
					</button>
				</div>
			</div>
		</div>
	);
};

export default PhotoCropper;
